import styled from 'styled-components'
import { BLOCKS, INLINES, Block, Inline } from '@contentful/rich-text-types'
import { defaultTheme } from '../styles/themes/defaultTheme'
import { Options } from '@contentful/rich-text-react-renderer'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useCourseStudyPlan } from './useCourseStudyPlan'
import { CodeBlock } from '../components/CodeBlock'
import { useMe } from '../pages/meeting-view/hooks/useMe'
import { useSessionParams } from './useSessionParams'
import { ContentStepDTO } from '../apis/api-content'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { YouTubeVideoLink } from '../components/ContentElements/YouTubeVideoLink'
import { QuoteElement } from '../components/ContentElements/QuoteElement'
import { ClickableImage } from '../components/ImageModal'

export const useContentfulRenderOptions = ({ step, status }: { step?: ContentStepDTO; status?: string }) => {

    const { user } = useMe()
    const { _classId, _profileId } = useSessionParams()
    const { findSessionByStepId } = useCourseStudyPlan(_classId)
    const session = findSessionByStepId(step?._id)
    const sessionId = session?.id

    const options: Options = {
        renderNode: {
            [BLOCKS.HR]: () => <hr style={{ border: '.5px solid rgba(255, 255, 255, 0.1)', margin: '1rem 0' }} />,
            [BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
                const type: string | undefined = node?.content?.[0]?.marks?.[0]?.type
                return type === 'code' ? (
                    <CodeBlock language={node?.content?.[0]?.data?.target?.fields?.language} children={children} />
                ) : (
                    <p children={children} />
                )
            },
            [BLOCKS.HEADING_1]: (_node: any, children: React.ReactNode) => <HeadingStyles><h1 children={children} /></HeadingStyles>,
            [BLOCKS.HEADING_2]: (_node: any, children: React.ReactNode) => <HeadingStyles><h2 children={children} /></HeadingStyles>,
            [BLOCKS.HEADING_3]: (_node: any, children: React.ReactNode) => <HeadingStyles><h3 children={children} /></HeadingStyles>,
            [BLOCKS.HEADING_4]: (_node: any, children: React.ReactNode) => <HeadingStyles><h4 children={children} /></HeadingStyles>,
            [BLOCKS.HEADING_5]: (_node: any, children: React.ReactNode) => <HeadingStyles><h5 children={children} /></HeadingStyles>,
            [BLOCKS.HEADING_6]: (_node: any, children: React.ReactNode) => <HeadingStyles><h6 children={children} /></HeadingStyles>,
            [BLOCKS.QUOTE]: (_node: any, children: React.ReactNode) => (
                <QuoteElement>
                    {children}
                </QuoteElement>
            ),
            [INLINES.HYPERLINK]: (node: any, children: React.ReactNode) => {
                const uri = node?.data?.uri
                const value = node?.content[0]?.value
                if (!uri || !value) return children
                const isForm = uri?.includes('typeform.com')
                const isNPS = uri?.includes('form_type=nps')

                const href = isForm
                    ? `${uri}&profile_id=${_profileId}&step_id=${step?._id}&module_id=unknown&section_id=${sessionId}&class_id=${_classId}&email=${user?.email}`
                    : uri

                if (isForm) return (
                    <DownloadableAsset href={`${href}`} target="_blank" rel="noopener noreferrer">
                        <AssetIcon>
                            <FontAwesomeIcon icon={['far', !isNPS ? 'ballot-check' : 'badge-check']} />
                        </AssetIcon>
                        <AssetInfo>
                            <AssetTitle>{value || 'Download file'}</AssetTitle>
                        </AssetInfo>
                    </DownloadableAsset>
                )


                // Check if it's a YouTube link
                if (uri.includes('youtube.com/watch') || uri.includes('youtu.be')) {
                    // Wrap in a div to avoid p > p nesting
                    return (
                        <YouTubeVideoLink url={uri} children={children} />
                    );
                }

                // Default link handling
                return (
                    <Link href={uri} target="_blank" rel="noopener noreferrer">
                        {children}
                    </Link>
                );
            },
            [INLINES.EMBEDDED_ENTRY]: (node: Block | Inline) => null,
            [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
                const contentType = node?.data?.target?.fields?.file?.contentType
                const href = node?.data?.target?.fields?.file?.url
                const title = node?.data?.target?.fields?.title
                const description = node?.data?.target?.fields?.description

                if (!contentType || !href) return null

                if (contentType.includes('image/')) {
                    return (
                        <ClickableImage 
                            src={`https:${href}`} 
                            alt={title || description || 'Image'} 
                            style={{ display: 'block', borderRadius: '8px', maxWidth: '100%' }}
                        />
                    )
                }

                if (contentType.includes('video/')) {
                    return (
                        <VideoContainer>
                            <video
                                controls
                                style={{
                                    width: '100%',
                                    borderRadius: '8px',
                                }}
                            >
                                <source src={`https:${href}`} type={contentType} />
                                Your browser does not support the video tag. Please use Google Chrome.
                            </video>
                            {title && <AssetCaption>{title}</AssetCaption>}
                        </VideoContainer>
                    )
                }

                if (contentType.includes('application/')) {
                    return (
                        <DownloadableAsset href={`https:${href}`} target="_blank" rel="noopener noreferrer">
                            <AssetIcon>
                                <FontAwesomeIcon icon={['far', 'file']} />
                            </AssetIcon>
                            <AssetInfo>
                                <AssetTitle>{title || 'Download file'}</AssetTitle>
                                {description && <AssetDescription>{description}</AssetDescription>}
                            </AssetInfo>
                            <DownloadIcon>
                                <FontAwesomeIcon icon={['fas', 'download']} />
                            </DownloadIcon>
                        </DownloadableAsset>
                    )
                }

                return null
            },
        },
    }

    return {
        options,
    }
}

const ExternalLink = ({ href, value, icon }: { href: string; value: string; icon?: IconProp }) => {
    return (
        <Link href={href} target="_blank" rel="noopener noreferrer" className="button outlined">
            {/* {icon && <Icon icon={icon} size="xl" />} */}
            {icon && <span>Placeholder</span>}
            <span>{value}</span>
        </Link>
    )
}

const Link = styled.a`
    text-decoration: underline !important;
    color: #00a3ff !important;
    display: inline !important;
    &:hover {
        color: #105df9 !important;
    }
    & > svg {
        margin: 0 0.2em 0 0 !important;
        height: 18px;
    }
`


const HeadingStyles = styled.div`
    & > h1{
        font-size: 24px !important;
    };
    & > h2{
        font-size: 21px !important;
    };
    & > h3{
        font-size: 18px !important;
    };
    & > h4{
        font-size: 16px !important;
    };
    & > h5{
        font-size: 14px !important;
    };
    & > h6{
        font-size: 12px !important;
    };
`


const VideoContainer = styled.div`
    margin: 1rem 0;
    width: 100%;
`

const AssetCaption = styled.p`
    margin-top: 0.5rem;
    text-align: center;
    font-size: 0.85rem;
    color: ${props => props.theme.color.electricBlue};
`

const DownloadableAsset = styled.a`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    text-decoration: none;
    color: white;
    padding: 0.75rem;
    margin: 1rem 0;
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;

    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
        border-color: ${props => props.theme.color.electricBlue};
    }
`

const AssetIcon = styled.div`
    font-size: 1.5rem;
    color: ${props => props.theme.color.electricBlue};
    display: flex;
    align-items: center;
    justify-content: center;
`

const AssetInfo = styled.div`
    flex: 1;
    overflow: hidden;
`

const AssetTitle = styled.h4`
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const AssetDescription = styled.p`
    margin: 0.25rem 0 0;
    font-size: 0.8rem;
    opacity: 0.8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const DownloadIcon = styled.div`
    font-size: 1rem;
    color: ${props => props.theme.color.electricBlue};
`